import type { TWOrganizationAIResponse } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWOrganizationAIResponse'
import type {
  CompanyManagementRequest,
  ManagementApplyPerson,
  ManagementApplyResult,
  ManagementDuplicateChoice,
  ManagementPersonMatches,
  ManagementPersonSuggestion,
  ManagementSuggestion,
  ManagementSuggestionValues,
} from '@/types/managementSuggestionTypes'

import { normalizeAiWeblinks } from '@/utilities/organizationAi'

// Pure state transitions of the AI management suggestions, shared by organization creation and the persons section

// The company search returns the official company name as name, which the generated type does not list
type CompanySearchResult = Partial<
  Pick<TWOrganizationAIResponse, 'addresses' | 'commercial_register' | 'weblinks'>
> & {
  name?: unknown
}

const getTrimmedText = (value: unknown): string | undefined => {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined
}

// Anchors the management search to the company the company search found, so both refer to the same company
const buildManagementSearchRequest = (
  organizationName: string,
  company: CompanySearchResult | null,
): CompanyManagementRequest => {
  const typedName = organizationName.trim()
  if (!company) return { organization_name: typedName }

  const anchor = {
    city: getTrimmedText(company.addresses?.find((address) => getTrimmedText(address.city))?.city),
    commercial_register: getTrimmedText(company.commercial_register),
    website: getTrimmedText(normalizeAiWeblinks(company.weblinks)[0]?.url),
  }

  return {
    organization_name: getTrimmedText(company.name) ?? typedName,
    ...Object.fromEntries(Object.entries(anchor).filter(([, value]) => value !== undefined)),
  }
}

const getUrlHost = (url: null | string | undefined): string => {
  if (!url || !/^https?:\/\//iu.test(url)) return ''

  try {
    return new URL(url).hostname.replace(/^www\./u, '')
  } catch {
    return ''
  }
}

const createManagementSuggestions = (
  persons: ManagementPersonSuggestion[],
): ManagementSuggestion[] => {
  return persons.map((person, index) => ({
    duplicateChoice: null,
    duplicates: person.duplicates,
    existingContact: person.existing_contact,
    id: index,
    matchesPending: false,
    sourceType: person.source_type,
    sourceUrl: person.source_url,
    status: person.existing_contact ? 'existing' : 'open',
    values: {
      first_name: person.first_name,
      last_name: person.last_name,
      position: person.position,
      role: person.role,
      salutation: person.salutation,
    },
  }))
}

const needsDuplicateChoice = (suggestion: ManagementSuggestion): boolean => {
  return suggestion.duplicates.length > 0 && suggestion.duplicateChoice === null
}

const canAcceptSuggestion = (suggestion: ManagementSuggestion): boolean => {
  return (
    suggestion.status === 'open' &&
    !suggestion.matchesPending &&
    !needsDuplicateChoice(suggestion) &&
    suggestion.values.first_name.trim() !== '' &&
    suggestion.values.last_name.trim() !== ''
  )
}

const updateSuggestion = (
  suggestions: ManagementSuggestion[],
  id: number,
  update: (suggestion: ManagementSuggestion) => ManagementSuggestion,
): ManagementSuggestion[] => {
  return suggestions.map((suggestion) => (suggestion.id === id ? update(suggestion) : suggestion))
}

const acceptSuggestion = (
  suggestions: ManagementSuggestion[],
  id: number,
): ManagementSuggestion[] => {
  return updateSuggestion(suggestions, id, (suggestion) =>
    canAcceptSuggestion(suggestion) ? { ...suggestion, status: 'accepted' } : suggestion,
  )
}

const discardSuggestion = (
  suggestions: ManagementSuggestion[],
  id: number,
): ManagementSuggestion[] => {
  return updateSuggestion(suggestions, id, (suggestion) =>
    ['accepted', 'open'].includes(suggestion.status)
      ? { ...suggestion, status: 'discarded' }
      : suggestion,
  )
}

const reopenSuggestion = (
  suggestions: ManagementSuggestion[],
  id: number,
): ManagementSuggestion[] => {
  return updateSuggestion(suggestions, id, (suggestion) =>
    ['accepted', 'discarded'].includes(suggestion.status)
      ? { ...suggestion, status: 'open' }
      : suggestion,
  )
}

const updateSuggestionValues = (
  suggestions: ManagementSuggestion[],
  id: number,
  values: ManagementSuggestionValues,
): ManagementSuggestion[] => {
  return updateSuggestion(suggestions, id, (suggestion) => {
    const trimmedValues = {
      ...values,
      first_name: values.first_name.trim(),
      last_name: values.last_name.trim(),
      position: values.position.trim(),
    }
    const nameChanged =
      trimmedValues.first_name !== suggestion.values.first_name ||
      trimmedValues.last_name !== suggestion.values.last_name

    // The duplicates and a chosen person belong to the old name
    return nameChanged
      ? { ...suggestion, duplicateChoice: null, matchesPending: true, values: trimmedValues }
      : { ...suggestion, values: trimmedValues }
  })
}

const setSuggestionMatches = (
  suggestions: ManagementSuggestion[],
  id: number,
  matches: ManagementPersonMatches,
): ManagementSuggestion[] => {
  return updateSuggestion(suggestions, id, (suggestion) => ({
    ...suggestion,
    duplicateChoice: null,
    duplicates: matches.duplicates,
    existingContact: matches.existing_contact,
    matchesPending: false,
    status: matches.existing_contact ? 'existing' : 'open',
  }))
}

const chooseDuplicate = (
  suggestions: ManagementSuggestion[],
  id: number,
  choice: ManagementDuplicateChoice,
): ManagementSuggestion[] => {
  return updateSuggestion(suggestions, id, (suggestion) => ({
    ...suggestion,
    duplicateChoice: choice,
  }))
}

const buildApplyPersons = (suggestions: ManagementSuggestion[]): ManagementApplyPerson[] => {
  return suggestions
    .filter((suggestion) => suggestion.status === 'accepted')
    .map((suggestion) => ({
      ...suggestion.values,
      action: suggestion.duplicateChoice?.action ?? 'create',
      source_type: suggestion.sourceType,
      source_url: suggestion.sourceUrl,
      ...(suggestion.duplicateChoice?.action === 'link'
        ? { contact: suggestion.duplicateChoice.contact }
        : {}),
    }))
}

const getSuggestionName = (suggestion: ManagementSuggestion): string => {
  return `${suggestion.values.first_name} ${suggestion.values.last_name}`.trim()
}

interface SaveResultSummary {
  createdCount: number
  failedNames: string[]
  linkedCount: number
  suggestions: ManagementSuggestion[]
}

// savedIds lists the suggestion ids in the order they were sent, so result.index points into it
const applySaveResults = (
  suggestions: ManagementSuggestion[],
  savedIds: number[],
  results: ManagementApplyResult[],
): SaveResultSummary => {
  let updatedSuggestions = suggestions
  const failedNames: string[] = []
  let createdCount = 0
  let linkedCount = 0

  for (const result of results) {
    const id = savedIds[result.index]
    const suggestion = updatedSuggestions.find((currentSuggestion) => currentSuggestion.id === id)

    if (id === undefined || !suggestion) {
      // Result of a person that was not sent; nothing to update
    } else if (result.status === 'failed') {
      failedNames.push(getSuggestionName(suggestion))
      updatedSuggestions = reopenSuggestion(updatedSuggestions, id)
    } else {
      // skipped: the person was linked to the organization meanwhile, so nothing changed
      const isSkipped = result.status === 'skipped'
      createdCount += result.status === 'created' ? 1 : 0
      linkedCount += result.status === 'linked' ? 1 : 0
      updatedSuggestions = updateSuggestion(updatedSuggestions, id, (current) => ({
        ...current,
        existingContact: result.contact,
        status: isSkipped ? 'existing' : 'saved',
      }))
    }
  }

  return { createdCount, failedNames, linkedCount, suggestions: updatedSuggestions }
}

export {
  acceptSuggestion,
  applySaveResults,
  buildApplyPersons,
  buildManagementSearchRequest,
  canAcceptSuggestion,
  chooseDuplicate,
  createManagementSuggestions,
  discardSuggestion,
  getSuggestionName,
  getUrlHost,
  needsDuplicateChoice,
  reopenSuggestion,
  type SaveResultSummary,
  setSuggestionMatches,
  updateSuggestionValues,
}
